import React, { useEffect, useState, useRef } from "react";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { PlusCircle, Copy, RefreshCw, X, Image, Loader2 } from "lucide-react";
import { auth, storage } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { canUseGoldFeature } from "../utils/featureAccess";
import toast, { Toaster } from "react-hot-toast";

// Replace Dialog with a custom modal component
const Modal = ({ open, onClose, children, className = "" }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl ${className}`}>
        {children}
      </div>
    </div>
  );
};

const categoryIcons = {
  Instagram: "instagram.png",
  Amazon: "amazon.png",
  Myntra: "myntra.png",
  Custom: "custom.png",
};

const SEOContentPage = () => {
  const [previewImage, setPreviewImage] = useState(null);
  const [generatedText, setGeneratedText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [garments, setGarments] = useState([]);
  const [assetTab, setAssetTab] = useState("models");
  const [isAssetImage, setIsAssetImage] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);


  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userInfo = docSnap.data();
          setUserId(user.uid);
          setUserData(userInfo);
  
          if (!canUseGoldFeature(userInfo)) {
            toast.error("SEO Content is only available in the Gold Plan.");
            navigate("/pricing");
          }
        }
      } else {
        setUserId(null);
        toast.error("You need to log in first.");
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, []);
  
  useEffect(() => {
    // const savedPreview = localStorage.getItem("previewImage");
    // if (savedPreview) setPreviewImage(savedPreview);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        fetchAssets(user.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchAssets = async (uid) => {
    const modelRef = ref(storage, `assets/${uid}/models`);
    const garmentRef = ref(storage, `assets/${uid}/garments`);
    const fetchImages = async (storageRef) => {
      const res = await listAll(storageRef);
      return await Promise.all(res.items.map((item) => getDownloadURL(item)));
    };

    setModels(await fetchImages(modelRef));
    setGarments(await fetchImages(garmentRef));
  };
  const deductCredits = async (amount = 2) => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const currentCredits = userSnap.data().credits || 0;

      await updateDoc(userRef, {
        credits: currentCredits - amount,
      });

      setUserData((prev) => ({
        ...prev,
        credits: currentCredits - amount,
      }));

      return true;
    } else {
      toast.error("User document not found.");
      return false;
    }
  } catch (err) {
    console.error("Failed to deduct credits:", err);
    toast.error("Error deducting credits.");
    return false;
  }
};
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Image = reader.result;
      localStorage.setItem("previewImage", base64Image);
      setPreviewImage(base64Image);
      setIsAssetImage(false);
      toast.success('Image uploaded successfully');
    };
    reader.readAsDataURL(file);
  };
  
  const handleAssetSelect = (url) => {
    localStorage.setItem("previewImage", url);
    setPreviewImage(url);
    setIsAssetImage(true); // ✅ Mark as asset image
    setShowModal(false);
    toast.success("Image selected from assets!");
  };
  
  const handleGenerateContent = async () => {
    if (!selectedCategory) {
      toast.error("Please select a category first.");
      return;
    }
  
    if (!previewImage) {
      toast.error("Please upload or select an image first.");
      return;
    }

    // ✅ Check user credits before generating
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      toast.error("User not found.");
      return;
    }

    const currentCredits = userSnap.data().credits || 0;
    const requiredCredits = 2; // SEO content generation costs 2 credits

    if (currentCredits < requiredCredits) {
      toast.error("Insufficient credits. Buy more.");
      return;
    }

    let prompt = "";
    switch (selectedCategory) {
      case "Instagram":
        prompt = "A user uploads a garment image. First, research the latest Instagram and fashion trends relevant to this garment’s style, cut, color palette, and design (e.g., trending colors, popular hashtags, styling aesthetics, influencer captions). Then, create a short, trendy caption that feels fashion-forward, engaging, and conversational. Use emojis naturally. Add 6-10 trending fashion/lifestyle hashtags (including seasonal or viral ones) at the end. The tone should align with Instagram fashion creators, keeping it fresh and on-trend.";
        break;
      case "Amazon":
        prompt = `Given an uploaded garment image, create Amazon India listing content in paragraph form. Include:
                    A concise, keyword-rich product title (within 200 characters)
                    A detailed product description written in a persuasive style, covering features, benefits, and material details
                    Feature highlights (woven as sentences rather than bullet points), such as fabric quality, size/fit options, wash care, target gender, age range, department, and color/size variants
                    Subtle SEO keywords naturally blended in
                    Ensure the output reads like an Amazon product description rather than just raw attributes.`;
        break;
      case "Myntra":
        prompt = `A user uploads an image of a garment (dress, top, t-shirt, etc.). Generate Myntra listing content in paragraph form. Include:
                      A crisp, attractive product title
                      A detailed product description highlighting design, fabric, comfort, and style
                      Specifications such as fabric type, sleeve length, fit, wash care, occasion, and ideal season.
                      Meta keywords for SEO (woven into the description naturally)
                      Make it sound like a Myntra product detail page — stylish yet informative`;
        break;
      case "Custom":
        if (!customPrompt.trim()) {
          toast.error("Please enter a custom prompt.");
          return;
        }
        prompt = customPrompt.trim();
        break;
      default:
        return;
    }
  
    try {
      setIsGenerating(true);
      setIsLoading(true);
  
      const response = await fetch("https://seocontent.styleuai.com/generate-seo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          image_url: previewImage,
        }),
      });
  
      const data = await response.json();
      setGeneratedText(data.result || "No response from API");
  
      if (response.ok) {
        toast.success("SEO content generated successfully!");
        await deductCredits(2); // ✅ Deduct 2 credits only if generation is successful
      } else {
        toast.error(data.error || "Failed to generate SEO content.");
      }

    } catch (error) {
      console.error("Error calling backend:", error);
      setGeneratedText("Something went wrong while generating content.");
      toast.error("Failed to generate content. Please try again.");
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedText);
      toast.success("Content copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy content");
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
  {/* Sidebar for desktop */}
  <div className="hidden md:block">
    <Sidebar />
  </div>

  <div className="flex-1 flex flex-col">
    <Header />

    {/* Sidebar for mobile */}
    <div className="block md:hidden">
      <Sidebar />
    </div>
        <Toaster position="top-right" />
       <div className="pricing-main">

          <div className="mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#3913C5] to-purple-600 bg-clip-text text-transparent">
              SEO Content Generator
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Create optimized content for your fashion products
            </p>
          </div>
          <hr className="my-4 border-gray-200" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            {/* Category Selection */}
          
             <div className="lg:col-span-3 bg-white/80 p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col min-h-[400px]">

                <h2 className="text-lg font-bold">Choose Category</h2>
                
                {/* Category Options */}
                <div className="flex flex-col flex-1 gap-4">
                  {["Instagram", "Amazon", "Myntra", "Custom"].map((name) => (
                    <button
                      key={name}
                      onClick={() => {
                        setSelectedCategory(name);
                        if (name !== "Custom") setCustomPrompt("");
                      }}
                      className={`flex items-center gap-4 w-full justify-center py-6 rounded-xl text-lg font-semibold transition-all duration-200 ${
                        selectedCategory === name
                          ? "bg-gradient-to-r from-[#3913C5] to-purple-600 text-white shadow-lg"
                          : "bg-white hover:bg-gray-50 border border-gray-200 text-gray-700"
                      }`}
                    >
                      <img src={categoryIcons[name]} alt={name} className="w-8 h-8" />
                      <span>{name}</span>
                    </button>
                  ))}
                </div>

                {/* Only show Custom prompt when selected */}
                {selectedCategory === "Custom" && (
                  <textarea
                    className="mt-4 w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#3913C5] text-sm resize-none"
                    rows={4}
                    placeholder="Enter your custom prompt..."
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                  />
                )}
              </div>


            {/* Image Upload/Preview */}
            <div className="lg:col-span-4 bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col h-[450px]">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold">Upload Image</h2>
                <button
                  onClick={() => setShowModal(true)}
                  className="text-sm sm:text-base text-[#3913C5] hover:text-[#2a0d8f] transition-colors flex items-center gap-1"
                >
                  <span>Choose from Assets</span>
                  <Image className="w-4 h-4" />
                </button>
              </div>
              <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 cursor-pointer hover:border-[#3913C5] transition-all duration-300 hover:bg-gray-50/50 relative overflow-hidden aspect-[3/4]"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />

              {previewImage ? (
                <>
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="absolute inset-0 w-full h-full object-contain rounded-xl"
                  />
                  {/* ADD THIS: Close Button */}
                  <button
                    className="absolute top-2 right-2 bg-white/80 p-1 rounded-full shadow hover:scale-110 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.removeItem("previewImage");
                      setPreviewImage(null);
                      setIsAssetImage(false);
                    }}
                  >
                    <X className="w-5 h-5 text-gray-700" />
                  </button>
                </>
              ) : (
                <>
                  <PlusCircle className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
                  <p className="text-sm sm:text-base text-gray-600 text-center">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    PNG, JPG, JPEG (max. 5MB)
                  </p>
                </>
              )}
            </div>
      
            </div>

            {/* Generated Content */}
            <div className="lg:col-span-5 bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col h-[450px]">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold">Generated Content</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyToClipboard}
                    disabled={!generatedText}
                    className="p-2 rounded-xl bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleGenerateContent}
                    disabled={isGenerating || !previewImage || !selectedCategory}
                    className="px-3 sm:px-4 py-2 bg-gradient-to-r from-[#3913C5] to-purple-600 text-white rounded-xl hover:from-[#2a0d8f] hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm sm:text-base">Generating...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        <span className="text-sm sm:text-base">Generate</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 animate-spin text-[#3913C5]" />
                </div>
              ) : generatedText ? (
                <div className="bg-white rounded-xl p-3 sm:p-4 h-[400px] overflow-y-auto shadow-inner">
                  <pre className="whitespace-pre-wrap text-sm sm:text-base text-gray-700">
                    {generatedText}
                  </pre>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 h-[400px] shadow-inner">
                <Image className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                <p className="text-sm sm:text-base text-gray-600 text-center">
                  Upload an image and select a category to generate content
                </p>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Asset Selection Modal */}
        <Modal
          open={showModal}
          onClose={() => setShowModal(false)}
          className="p-4 sm:p-6 max-w-4xl w-full"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg sm:text-xl font-semibold">Select from Assets</h3>
            <button
              onClick={() => setShowModal(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setAssetTab("models")}
              className={`px-3 py-1 rounded-xl transition-all duration-200 ${
                assetTab === "models"
                  ? "bg-gradient-to-r from-[#3913C5] to-purple-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Models
            </button>
            <button
              onClick={() => setAssetTab("garments")}
              className={`px-3 py-1 rounded-xl transition-all duration-200 ${
                assetTab === "garments"
                  ? "bg-gradient-to-r from-[#3913C5] to-purple-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Garments
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[60vh] overflow-y-auto">
            {(assetTab === "models" ? models : garments).map((url, index) => (
              <div
                key={index}
                className="relative aspect-[3/4] rounded-xl overflow-hidden group cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
                onClick={() => handleAssetSelect(url)}
              >
                <img
                  src={url}
                  alt={`Asset ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="text-white text-sm bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full">
                    Select
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Modal>

        {/* Image Preview Modal */}
        <Modal
          open={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          className="p-4 sm:p-6 max-w-4xl w-full"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg sm:text-xl font-semibold">Image Preview</h3>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              className="w-full max-h-[70vh] object-contain rounded-xl shadow-inner"
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default SEOContentPage;
